// src/security/v2/types.ts
//
// TypeScript types for the Permissions v2 data layer (ADR-001 / DEV-776).
// Mirrors the shapes returned by PermissionsController on the backend.

import type { CapabilityName } from "./generated/capabilityNames";

export type GUID = string;

/**
 * Capability name, e.g. "impact:view", "risks:edit", "collection:view".
 *
 * DEV-746: this is now a literal-string union generated from the
 * migration seeds. The runtime evaluator still works on strings, but
 * every static `hasCapability(...)` call site gets compile-time
 * guarantees that the capability is one the system understands.
 * Regenerate via `node scripts/generate-capability-names.cjs`.
 */
export type {
  CapabilityName,
  ProjectCapabilityName,
  CollectionCapabilityName,
  CommunityCapabilityName,
} from "./generated/capabilityNames";
export {
  CAPABILITY_REGISTRY,
  CAPABILITY_NAMES,
  isKnownCapability,
} from "./generated/capabilityNames";

/** Scope types a grant or capability can target. All three are live in v1. */
export type ScopeType = "project" | "collection" | "community";

/** Grant mode — how the user acquires the bundle. */
export type GrantMode = "user" | "slot";

/**
 * Well-known GUIDs for the platform bundles. Mirrors the backend constants
 * in `Models/PermissionSet.cs::PlatformPermissionSets`. Use these in
 * code rather than name-matching — bundle names are localized for display
 * but GUIDs are stable across all environments and locales.
 *
 * Stored uppercase to align with normalizeGuid output (so direct string
 * comparison works without case folding).
 */
export const PLATFORM_PERMISSION_SET_GUIDS = {
  Owner: "5E700001-0000-0000-0000-000000000001",
  Admin: "5E700001-0000-0000-0000-000000000002",
  Editor: "5E700001-0000-0000-0000-000000000003",
  Contributor: "5E700001-0000-0000-0000-000000000004",
  RiskManager: "5E700001-0000-0000-0000-000000000005",
  Viewer: "5E700001-0000-0000-0000-000000000006",
  ExternalCollaborator: "5E700001-0000-0000-0000-000000000007",
  // DEV-1034 phase 2 — added in migration 157 alongside the
  // Viewer→Observer rename for project-scope governance.
  Auditor: "5E700001-0000-0000-0000-000000000008",
} as const;

// ── API DTOs (match PermissionsController JSON shapes) ───────

export interface CapabilityDto {
  name: CapabilityName;
  scopeType: ScopeType;
  area: string;
  description: string;
  isAssignable: boolean;
}

export interface PermissionSetDto {
  permissionSetGuid: GUID;
  name: string;
  description: string;
  ownershipScope: "platform" | "project";
  projectGuid: GUID | null;
  isSystem: boolean;
  capabilities: CapabilityName[];
  /**
   * DEV-871 [OO-C]. When true, evaluator skips this bundle's caps for users
   * without 2FA enrolled. The grant exists as a data row but is dormant
   * until 2FA enrollment activates it. Backed by permission_sets.requires_2fa
   * (migration 129). Optional in the wire shape for backward compatibility
   * with serialized payloads from before the column existed.
   */
  requires2fa?: boolean;
  /**
   * DEV-859 [D2]. When true, holding this bundle at any community scope C
   * confers every assignable community-scope capability on C and every
   * descendant of C (Rule 1.5 — scoped sysadmin / Org Admin marker).
   * Backed by permission_sets.is_scoped_sysadmin (migration 129).
   */
  isScopedSysadmin?: boolean;
  /**
   * DEV-1036. How this bundle is granted. "user" = governance (granted
   * directly to a user, e.g. Owner / Admin / Viewer / Contributor); "slot"
   * = operational (granted to a project role slot, e.g. Editor / Risk
   * Manager / External Collaborator). The Project Access tab's Owners &
   * Admins section filters bundles to "user"; the Teams tab's slot editor
   * filters to "slot". Backed by permission_sets.grant_mode (migration
   * 156). Optional in the wire shape for backward compatibility with
   * serialized payloads from before the column existed; consumers default
   * to "user" when undefined.
   */
  grantMode?: GrantMode;
}

/** DEV-892. Grant lifecycle status. Evaluator only honors 'active'. */
export type GrantStatus = "pending" | "active" | "declined" | "revoked";

export interface PermissionGrantDto {
  grantGuid: GUID;
  permissionSetGuid: GUID;
  mode: GrantMode;
  roleSlotGuid: GUID | null;
  userGuid: GUID | null;
  scopeType: ScopeType;
  scopeGuid: GUID;
  grantedAt: string;
  expiresAt: string | null;
  /**
   * DEV-892. Lifecycle status. Optional in the wire shape for backward
   * compatibility with serialized payloads from before the column existed —
   * the evaluator treats undefined as 'active' to preserve old behavior.
   */
  status?: GrantStatus;
}

export interface MyGrantsResponse {
  userGuid: GUID;
  activeSlotGuids: GUID[];
  directGrants: PermissionGrantDto[];
  slotGrants: PermissionGrantDto[];
}

// ── Evaluator surface ────────────────────────────────────────

/**
 * Inputs to <see cref="evaluateSecurityForUserV2"/>. Kept pure — the evaluator
 * does not fetch data. Callers assemble this object and pass it in.
 */
export interface EvaluationInputs {
  userGuid: GUID;
  isSystemAdmin: boolean;
  /** Capability catalog from /api/permissions/capabilities. */
  capabilities: CapabilityDto[];
  /** Bundles from /api/permissions/permission-sets. */
  permissionSets: PermissionSetDto[];
  /** Current user's grants from /api/permissions/my-grants. */
  myGrants: MyGrantsResponse;
  /** Which collections the project belongs to (from /api/collections/by-project or similar). */
  collectionGuidsForProject: GUID[];
  /** Project visibility ("public" | "private"). */
  projectVisibility: "public" | "private";
}

export interface DebugRuleTrace {
  rule: string;
  matched: boolean;
  capsAdded: CapabilityName[];
  note?: string;
}

export interface EvaluationResult {
  userGuid: GUID;
  projectGuid: GUID;
  capabilities: Set<CapabilityName>;
  debug?: DebugRuleTrace[];
}

// ── Community-scope evaluator surface (DEV-859 / DEV-854) ────

/**
 * Inputs for <see cref="evaluateCommunitySecurityV2"/>. Distinct from
 * EvaluationInputs (project-focused) because community evaluation needs the
 * ancestor chain (for Rule 1.5 + Rule 6) and doesn't need project-collection
 * membership or project visibility.
 */
export interface CommunityEvaluationInputs {
  userGuid: GUID;
  isSystemAdmin: boolean;
  /**
   * Whether the user has 2FA (TOTP) enrolled. Bundles flagged
   * requires2fa=true contribute no caps when this is false.
   */
  is2FAEnabled: boolean;
  capabilities: CapabilityDto[];
  permissionSets: PermissionSetDto[];
  myGrants: MyGrantsResponse;
  /**
   * Ancestor chain from the target community's PARENT up to root.
   * Empty when target is itself an org root. Nearest ancestor first;
   * root last. Used by Rule 1.5 (find nearest scoped-sysadmin grant)
   * and Rule 6 (walk for cascadable _descendants caps).
   */
  ancestorCommunityGuidsForCommunity: GUID[];
}

export interface CommunityEvaluationResult {
  userGuid: GUID;
  communityGuid: GUID;
  capabilities: Set<CapabilityName>;
  debug?: DebugRuleTrace[];
}

// ── Constants ────────────────────────────────────────────────

/**
 * ADR-001 §Collections cascade. These 6 view capabilities cascade from a
 * collection:view grant to every project in that collection. Also doubles
 * as the default set for Public-visibility projects (ADR-001 open amendment
 * captured in DEV-740 closeout).
 */
export const CASCADABLE_SET: ReadonlySet<CapabilityName> = new Set([
  "impact:view",
  "about:view",
  "expert:view",
  "forum:view",
  "media:view",
  "performance:view",
]);
